import React, { useState } from 'react';
import { formatInr } from './checkout';

/* ==================================================================
   RevenueChart
   ------------------------------------------------------------------
   Monthly revenue, split by the two products. A stacked column: the
   data's job is change-over-time AND composition, and the two parts
   sum to something meaningful (total revenue), which is the one case
   stacking is honest.

   Colours are HyrSkill's own tokens, not new ones:
     profile → --primary     #4F46E5
     job     → --accent-dark #059669
   That exact pair was run through the palette validator and passes
   all six checks against a light surface — including contrast, which
   the lighter --accent (#10B981) fails at 2.47:1.

   Mark specs held here: columns capped at 24px (the rest of the band
   is air), a 4px rounded cap on the topmost segment only and square
   at the baseline, and a 2px surface-coloured gap doing the
   separating between stacked segments rather than a border.

   Identity never rests on colour alone: a legend is always present,
   the hovered column names both figures, and the payments table below
   is the table view of the same data.
   ================================================================== */

const SERIES = [
  { key: 'profile_paise', label: 'Profile access', color: 'var(--primary)' },
  { key: 'job_paise', label: 'Job posts', color: 'var(--accent-dark)' },
];

/** Round a max up to a clean axis top so ticks read 0 / 5,000 / 10,000. */
function niceMax(value) {
  if (value <= 0) return 1000;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export default function RevenueChart({ series = [] }) {
  const [hovered, setHovered] = useState(null);

  const hasData = series.some((b) => b.total_paise > 0);
  const max = niceMax(Math.max(...series.map((b) => b.total_paise), 0));

  if (!series.length) return null;

  return (
    <div className="bl-chart">
      <div className="bl-chart-head">
        <div>
          <h3 className="bl-chart-title">Revenue over time</h3>
          <p className="bl-chart-sub">Successful payments only. Refunds are reported separately.</p>
        </div>
        {/* Always present for two series — colour is never the sole identity channel. */}
        <ul className="bl-legend">
          {SERIES.map((s) => (
            <li key={s.key}>
              <span className="bl-legend-dot" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </li>
          ))}
        </ul>
      </div>

      {!hasData ? (
        <div className="bl-empty">
          <div className="bl-empty-title">No revenue yet</div>
          <p>Successful payments will be charted here.</p>
        </div>
      ) : (
        <div className="bl-chart-body">
          {/* Ticks carry the values that are not directly labelled. */}
          <div className="bl-chart-axis" aria-hidden="true">
            <span>{formatInr(max)}</span>
            <span>{formatInr(max / 2)}</span>
            <span>₹0</span>
          </div>

          <div className="bl-chart-plot">
            {series.map((bucket) => {
              const isHovered = hovered === bucket.month;
              const profilePct = max ? (bucket.profile_paise / max) * 100 : 0;
              const jobPct = max ? (bucket.job_paise / max) * 100 : 0;

              return (
                <div
                  key={bucket.month}
                  className="bl-chart-band"
                  onMouseEnter={() => setHovered(bucket.month)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(bucket.month)}
                  onBlur={() => setHovered(null)}
                  tabIndex={0}
                  // The whole month reads as one figure to a screen reader,
                  // rather than two unlabelled rectangles.
                  aria-label={`${bucket.label}: ${formatInr(bucket.total_paise)} total — ${formatInr(bucket.profile_paise)} profile access, ${formatInr(bucket.job_paise)} job posts`}
                >
                  {isHovered && bucket.total_paise > 0 && (
                    <div className="bl-chart-tip" role="tooltip">
                      <strong>{bucket.label}</strong>
                      <span><i style={{ background: 'var(--primary)' }} />Profile access {formatInr(bucket.profile_paise)}</span>
                      <span><i style={{ background: 'var(--accent-dark)' }} />Job posts {formatInr(bucket.job_paise)}</span>
                      <span className="bl-chart-tip-total">Total {formatInr(bucket.total_paise)}</span>
                    </div>
                  )}

                  <div className="bl-chart-stack">
                    {/* Job sits on top, so it takes the rounded cap. */}
                    {jobPct > 0 && (
                      <span
                        className="bl-chart-seg bl-chart-seg--cap"
                        style={{ height: `${jobPct}%`, background: 'var(--accent-dark)' }}
                      />
                    )}
                    {profilePct > 0 && (
                      <span
                        className={`bl-chart-seg${jobPct > 0 ? '' : ' bl-chart-seg--cap'}`}
                        style={{ height: `${profilePct}%`, background: 'var(--primary)' }}
                      />
                    )}
                  </div>

                  <span className="bl-chart-label">{bucket.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
